/* ═══════════════════════════════════════════════════════════════
   STARDUST — by Afnan
   Hero video → wordmark materialize → scroll unlock → chapters.
   GSAP + ScrollTrigger + Lenis. Degrades gracefully on mobile /
   reduced-motion / video-autoplay failure.
   ═══════════════════════════════════════════════════════════════ */
(() => {
  "use strict";

  /* every load starts the journey from the top — no mid-page restore */
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  window.scrollTo(0, 0);


  const FILM_SPEED = 1;      // intro film playback rate; the 2.9s cut is already brisk

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isMobile = window.matchMedia("(max-width: 760px)").matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  // Arabic pages set dir="rtl" on <html> (the theme handles that); everything
  // CSS-driven follows automatically via logical properties, but this one
  // JS-driven horizontal scroll (the pinned gallery) needs to know explicitly
  const isRTL = getComputedStyle(document.documentElement).direction === "rtl";

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];

  const body = document.body;
  const hero = $("#hero");
  const video = $("#heroVideo");
  const cue = $("#scrollCue");

  /* video URLs come from the hero section settings in the theme editor */
  const CDN_VIDEO = (hero && hero.dataset.video) || "";
  const CDN_VIDEO_MOBILE = (hero && hero.dataset.videoMobile) || "";

  /* materials.js drives the glass preloader independently (it has its own
     font-load/min-beat timers) and fires this event when it's actually
     folded away. The video/wordmark sequence below used to run on its own
     clock too — fine when everything loads fast locally, but on a heavier
     real page our scripts (loaded last, after the whole rest of the
     document) can start late enough that the video already finished before
     the glass has even begun to open, so the curtain ends up parting to
     reveal content that's already fully visible. Wait for both. */
  const whenPreloaded = (() => {
    const el = $("#preload");
    if (!el || el.classList.contains("is-gone")) return () => Promise.resolve();
    const p = new Promise((resolve) => {
      document.addEventListener("stardust:preloaded", resolve, { once: true });
      setTimeout(resolve, 2500); // backstop if materials.js ever fails to fire it
    });
    return () => p;
  })();

  /* Opens the theme's own cart drawer after a successful add. The drawer is
     a custom element (<cart-drawer>) with a private, undocumented open API
     that changed between theme versions — calling it directly is fragile.
     What never changes is the header's own cart link/icon: real shoppers
     open the drawer by clicking it, so the theme is guaranteed to intercept
     that click correctly (no page navigation) and open the drawer with its
     real content already rendered. We just simulate that same click. */
  const openCartDrawer = () => {
    const link = $$('a[href$="/cart"]').find((a) => !a.closest("cart-drawer, #cart-drawer, .cart-drawer"));
    if (link) { link.click(); return true; }
    return false;
  };

  /* hero quick add-to-cart — plain fetch, no GSAP dependency, so it's wired
     up unconditionally here (above the degrade check below) and works even
     if GSAP/ScrollTrigger/Lenis never load. Only exists on Shopify: the
     button is only rendered when the section has a product configured. */
  const initHeroAddToCart = () => {
    const btn = $("#heroAddToCart");
    if (!btn) return;
    const label = $(".cta__label", btn);
    const defaultLabel = label.textContent;
    let busy = false;
    btn.addEventListener("click", async () => {
      if (busy || btn.disabled) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      busy = true;
      btn.disabled = true;
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => null))?.description || res.statusText);
        // most Online Store 2.0 themes listen for one of these to refresh their
        // own cart icon/drawer; harmless no-op if this theme uses neither
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:build", { bubbles: true }));
        // give the theme a beat to refresh its own cart data from the fetch
        // above, then open the drawer the same way a real click would
        setTimeout(openCartDrawer, 50);
        label.textContent = btn.dataset.addedLabel || "Added";
        btn.classList.add("is-added");
      } catch (err) {
        console.error("Stardust: add to cart failed —", err.message || err);
        label.textContent = btn.dataset.errorLabel || "Try again";
      } finally {
        setTimeout(() => {
          label.textContent = defaultLabel;
          btn.classList.remove("is-added");
          btn.disabled = false;
          busy = false;
        }, 1800);
      }
    });
  };
  initHeroAddToCart();

  /* if any CDN library failed, degrade to a fully static readable page */
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined" || typeof Lenis === "undefined") {
    body.classList.add("no-motion");
    body.classList.remove("is-locked", "is-hero");
    hero.classList.add("show-endframe", "is-ended", "is-ready");
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.clearScrollMemory("manual"); // refresh always restarts at the hero

  /* the wordmark is the supplied chrome lettering, one image */
  const wmPaths = $$("#wm .wm-img");

  /* ── Lenis smooth scroll (created now, started on unlock) ── */
  let lenis = null;
  const startScroll = () => {
    body.classList.remove("is-locked");
    if (reduceMotion) return; // native scroll for reduced motion
    lenis = new Lenis({ lerp: 0.18, wheelMultiplier: 1, smoothWheel: true });
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  };

  /* Lenis re-implements scrolling itself (it doesn't just rely on the
     browser's native overflow), so when the theme's own cart/menu drawer
     opens and tries to lock the page by setting overflow:hidden, Lenis
     ignores that and keeps scrolling the page behind it. Pause Lenis
     whenever any theme drawer/dialog has its `open` attribute set. */
  const pauseScrollForOpenDialogs = () => {
    const isAnyOpen = () => $$("[open]").some((el) => el.matches("dialog, cart-drawer, [class*='drawer'], [class*='modal']"));
    const sync = () => {
      if (!lenis) return;
      if (isAnyOpen()) lenis.stop();
      else lenis.start();
    };
    new MutationObserver(sync).observe(document.body, {
      attributes: true, attributeFilter: ["open"], subtree: true,
    });
    sync();
  };
  pauseScrollForOpenDialogs();

  /* ─────────────────────────── HERO SEQUENCE ─────────────────────────── */

  let heroDone = false;

  const dustField = () => {
    // gold specks that converge toward the wordmark as it forms
    const wrap = $(".wm__dust");
    if (!wrap || reduceMotion) return gsap.timeline();
    const tl = gsap.timeline();
    const N = isMobile ? 8 : 14;
    for (let i = 0; i < N; i++) {
      const s = document.createElement("i");
      wrap.appendChild(s);
      const x0 = gsap.utils.random(-6, 106);
      const y0 = gsap.utils.random(-30, 130);
      gsap.set(s, { left: x0 + "%", top: y0 + "%", scale: gsap.utils.random(0.5, 1.4) });
      tl.fromTo(s,
        { opacity: 0, xPercent: 0, yPercent: 0 },
        {
          opacity: gsap.utils.random(0.5, 0.95),
          x: (50 - x0) * gsap.utils.random(1.5, 3.5),
          y: (50 - y0) * gsap.utils.random(1.2, 2.4),
          duration: gsap.utils.random(1.4, 2.2),
          ease: "power2.inOut",
        }, gsap.utils.random(0, 0.5))
        .to(s, { opacity: 0, duration: 0.5 }, ">-0.35");
    }
    return tl;
  };

  const revealWordmark = () => {
    if (heroDone) return;
    heroDone = true;

    hero.classList.add("is-ended", "is-ready");

    if (reduceMotion) {
      gsap.set(wmPaths, { opacity: 1 });
      gsap.set([".hero__byline", "#scrollCue", ".hero__buy"], { opacity: 1 });
      startScroll();
      body.classList.remove("is-hero");
      initChapters();
      return;
    }

    // scroll unlocks the moment the wordmark starts forming — nobody waits for
    // the animation to finish before they can move on. But that "forming"
    // moment itself waits for the glass to actually be parting, so the
    // curtain never opens onto a hero that already finished revealing.
    whenPreloaded().then(() => {
      startScroll();
      initChapters();

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.add(dustField().timeScale(1.6), 0);

      // wordmark materializes: blur → sharp, slight scale settle, soft y drift,
      // uncovered from the centre outward so it still reads as letters arriving
      tl.fromTo(wmPaths,
        { opacity: 0, filter: "blur(14px)", scale: 1.08, y: 22, clipPath: "inset(0 50% 0 50%)", transformOrigin: "50% 50%" },
        {
          opacity: 1, filter: "blur(0px)", scale: 1, y: 0, clipPath: "inset(0 0% 0 0%)",
          duration: 1.05, ease: "power3.inOut",
        }, 0.05);

      tl.to(".hero__byline", { opacity: 1, y: 0, duration: 0.6 }, "-=0.7");
      tl.to(".hero__buy", { opacity: 1, y: 0, duration: 0.6 }, "-=0.5");
      tl.to(cue, { opacity: 1, duration: 0.6 }, "-=0.3");
    });
  };

  let heroInitStarted = false;
  const initHeroVideo = () => {
    if (heroInitStarted) return;
    heroInitStarted = true;
    if (reduceMotion) {
      // no autoplay motion — hold on the end frame and show the mark
      hero.classList.add("show-endframe");
      revealWordmark();
      return;
    }

    const src = (isMobile || saveData) ? (CDN_VIDEO_MOBILE || CDN_VIDEO) : CDN_VIDEO;
    if (!src) { hero.classList.add("show-endframe"); revealWordmark(); return; }
    video.src = src;

    let started = false;

    const fail = () => {
      if (started || heroDone) return;
      hero.classList.add("show-endframe");
      revealWordmark();
    };

    // if buffering drags on (slow network / autoplay blocked), skip the film
    const failTimer = setTimeout(fail, 3000);

    video.addEventListener("playing", () => {
      started = true;
      clearTimeout(failTimer);
      hero.classList.add("is-ready");
    }, { once: true });

    // the film runs a touch faster, and the wordmark starts just before it ends
    video.defaultPlaybackRate = video.playbackRate = FILM_SPEED;
    video.addEventListener("ended", () => revealWordmark(), { once: true });
    video.addEventListener("timeupdate", () => {
      if (video.duration && video.duration - video.currentTime < 0.6) revealWordmark();
    });

    // anyone who scrolls, swipes or presses a key during the film skips straight to the page
    const skip = () => { if (!heroDone) revealWordmark(); };
    ["wheel", "touchmove", "keydown"].forEach((e) => window.addEventListener(e, skip, { once: true, passive: true }));
    video.addEventListener("error", fail); // video failed → hold the end frame

    const p = video.play();
    if (p && p.catch) p.catch(() => fail());
  };

  /* ─────────────────────────── CHAPTERS ─────────────────────────── */

  let chaptersInit = false;
  const initChapters = () => {
    if (chaptersInit) return;
    chaptersInit = true;

    // leaving the hero → header chrome appears
    ScrollTrigger.create({
      trigger: "#flacon",
      start: "top 85%",
      onEnter: () => body.classList.remove("is-hero"),
      onLeaveBack: () => body.classList.add("is-hero"),
    });

    // top progress hairline
    gsap.to(".progress__bar", {
      scaleX: 1, ease: "none",
      scrollTrigger: { trigger: "#main", start: "top top", end: "bottom bottom", scrub: 0.4 },
    });

    // chapter rules draw in
    $$(".chapter__rule").forEach((rule) => {
      gsap.to(rule, {
        scaleX: 1, duration: 1.1, ease: "power3.inOut",
        scrollTrigger: { trigger: rule, start: "top 88%" },
      });
    });

    if (reduceMotion) {
      // static site: everything visible, no scroll effects
      gsap.set(".mask__inner", { y: 0, opacity: 1, filter: "blur(0px)" });
      gsap.set(".reveal-frame .frame__clip", { clipPath: "inset(0 0 0% 0)" });
      gsap.set(".reveal-frame .frame__clip img", { scale: 1 });
      gsap.set(".origin__hash", { opacity: 1, y: 0 });
      gsap.set("[data-rise]", { opacity: 1, y: 0 });
      gsap.set(".chapter__rule", { scaleX: 1 });
      initFlaconFilm(true);
      return;
    }

    initMasks();
    initFrames();
    initOrigin();

    initFlaconFilm(false);
    initNotesB();
    initGalleryB();
    initCtaBands();
    initCtaPanel();
    initFloatCta();
  };

  /* shared — masked lines resolve on their own approach; [data-rise] blocks follow */
  const initMasks = () => {
    $$(".mask__inner").forEach((line) => {
      gsap.to(line, {
        opacity: 1, filter: "blur(0px)", y: 0,
        duration: 1.2, ease: "power3.out",
        scrollTrigger: { trigger: line.closest(".mask") || line, start: "top 88%" },
      });
    });
    $$("[data-rise]").forEach((el) => {
      gsap.from(el, {
        opacity: 0, y: 28, duration: 1.1, ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 92%" },
      });
    });
  };

  /* shared — clip-path frame reveals + inner parallax */
  const initFrames = () => {
    $$(".reveal-frame").forEach((frame) => {
      const clip = $(".frame__clip", frame);
      const img = $("img", clip);
      const fromLeft = frame.dataset.reveal === "left";

      gsap.to(clip, {
        clipPath: fromLeft ? "inset(0 0% 0 0)" : "inset(0 0 0% 0)",
        duration: 1.5, ease: "power4.inOut",
        scrollTrigger: { trigger: frame, start: "top 82%" },
      });

      if (!img) return; // frame holds a <video> — no inner parallax

      const drift = parseFloat(img.dataset.parallax) || 1.14;
      gsap.fromTo(img, { scale: drift, yPercent: -4 }, {
        scale: Math.max(1.02, drift - 0.1), yPercent: 4, ease: "none",
        scrollTrigger: { trigger: frame, start: "top bottom", end: "bottom top", scrub: 0.6 },
      });
    });
  };

  /* 03 — origin: bg parallax, copy line reveals, drifting dust */
  const initOrigin = () => {
    $$("[data-parallax-bg]").forEach((img) => {
      gsap.fromTo(img, { yPercent: -9 }, {
        yPercent: 9, ease: "none",
        scrollTrigger: { trigger: img.closest("section"), start: "top bottom", end: "bottom top", scrub: 0.5 },
      });
    });

    $$(".origin [data-lines]").forEach((el) => {
      gsap.from(el, {
        opacity: 0, y: 44, duration: 1.3, ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 86%" },
      });
    });
    gsap.to(".origin__hash", {
      opacity: 1, y: 0, duration: 1.1, ease: "power3.out",
      scrollTrigger: { trigger: ".origin__hash", start: "top 94%" },
    });

    initDust();
  };

  /* subtle drifting gold dust over the origin photo (canvas, ~cheap) */
  const initDust = () => {
    const canvas = $("#dustCanvas");
    if (!canvas || isMobile) return;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const P = [];
    const N = 42;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      w = canvas.width = r.width * devicePixelRatio;
      h = canvas.height = r.height * devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < N; i++) {
      P.push({
        x: Math.random() * 2 - 1, y: Math.random() * 2 - 1,
        r: (Math.random() * 1.6 + 0.5) * devicePixelRatio,
        vx: (Math.random() - 0.5) * 0.00022,
        vy: -(Math.random() * 0.00028 + 0.00008),
        a: Math.random() * 0.45 + 0.1,
        tw: Math.random() * Math.PI * 2,
      });
    }

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = "#E9C77F";
      P.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.y < -1.05) { p.y = 1.05; p.x = Math.random() * 2 - 1; }
        if (p.x < -1.05) p.x = 1.05; else if (p.x > 1.05) p.x = -1.05;
        const tw = 0.55 + 0.45 * Math.sin(t * 0.0012 + p.tw);
        ctx.globalAlpha = p.a * tw;
        ctx.beginPath();
        ctx.arc((p.x * 0.5 + 0.5) * w, (p.y * 0.5 + 0.5) * h, p.r, 0, 7);
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };

    // only run while the section is on screen
    ScrollTrigger.create({
      trigger: "#origin", start: "top bottom", end: "bottom top",
      onEnter: () => (raf = requestAnimationFrame(draw)),
      onEnterBack: () => (raf = requestAnimationFrame(draw)),
      onLeave: () => cancelAnimationFrame(raf),
      onLeaveBack: () => cancelAnimationFrame(raf),
    });
  };


  /* 02b — flacon "held to the light": plain autoplay/loop while the panel is in view.
     Runs for every presentation option on the review page. */
  const initFlaconFilm = (staticMode) => {
    $$(".js-flacon-scrub").forEach((v) => {
      const wrap = v.closest(".flacon__film");
      if (!v.dataset.src) return;   // no video set in the flacon section
      v.loop = true;
      v.src = v.dataset.src;

      if (staticMode) return; // poster only under reduced motion

      ScrollTrigger.create({
        trigger: wrap, start: "top 90%", end: "bottom 10%",
        onEnter: () => v.play().catch(() => {}),
        onEnterBack: () => v.play().catch(() => {}),
        onLeave: () => v.pause(),
        onLeaveBack: () => v.pause(),
      });
    });
  };

  /* notes option B — band text rises as each band enters */
  const initNotesB = () => {
    $$(".notes-b .band").forEach((band) => {
      gsap.from($(".band__text", band), {
        opacity: 0, y: 40, duration: 1.1, ease: "power3.out",
        scrollTrigger: { trigger: band, start: "top 70%" },
      });
    });
  };

  /* gallery option B — pinned horizontal strip (desktop only) */
  const initGalleryB = () => {
    if (isMobile) return; // native horizontal scroll via CSS
    const track = $("#galBTrack");
    if (!track) return;
    const dist = () => track.scrollWidth - window.innerWidth;
    gsap.to(track, {
      x: () => (isRTL ? dist() : -dist()), // RTL: the strip starts flush right, so it travels the other way
      ease: "none",
      scrollTrigger: {
        trigger: "#galleryB",
        start: "top top",
        end: () => "+=" + dist(),
        pin: "#galBPin",
        scrub: 0.6,
        invalidateOnRefresh: true,
      },
    });
  };

  /* close option B — masked line reveals + cta */
  const initCtaPanel = () => {
    const panel = $(".cta-panel");
    if (!panel) return;
    gsap.from(".cta-panel__body > *", {
      opacity: 0, y: 24, duration: 1, ease: "power3.out", stagger: 0.09,
      scrollTrigger: { trigger: panel, start: "top 78%" },
    });
  };

  /* floating shop button: appears once the hero is passed, steps aside for the close CTA */
  const initFloatCta = () => {
    const btn = $("#floatCta");
    if (!btn) return;
    ScrollTrigger.create({
      trigger: ".flacon", start: "top 55%",
      onEnter: () => btn.classList.add("is-on"),
      onLeaveBack: () => btn.classList.remove("is-on"),
    });
    ScrollTrigger.create({
      trigger: ".buy", start: "top 75%",
      onEnter: () => btn.classList.add("is-suppressed"),
      onLeaveBack: () => btn.classList.remove("is-suppressed"),
    });
  };

  const initCtaBands = () => {
    $$(".cta-band").forEach((band) => {
      gsap.from(band.children, {
        opacity: 0, y: 22, duration: 1, ease: "power3.out", stagger: 0.12,
        scrollTrigger: { trigger: band, start: "top 85%" },
      });
    });
  };


  /* ── boot ── */
  initHeroVideo();
  // safety net: never trap the user behind a stalled hero
  setTimeout(() => { if (!heroDone) revealWordmark(); }, 12000);
})();
